import { logger } from '~/modules/utilities/logger';
import { SectionTypes } from '~/schemas/content-schema-v1';
import { AnalyticsEventTopic, getOafIdFromEvent, MORE_RESOURCES_EVENT_CONTEXT } from '../analyticsHelper';
import { getChannel } from '../channel';

export interface MarketingAnalyticsEvent {
    listing?: string;
    oafListingId: string;
    topic: string;
    event_type?: string;
    authenticated: string;
    channel: string;
    parent_channel: string;
    viewport_height: string;
    viewport_width: string;
    device_height: string;
    device_width: string;
    browser: string;
    language: string;
    url: string;
    referrer: string;
    primary_key: string;
    token: string;
    timestamp: string;
    date: string;
    trace: boolean;
    placement_id: string;
    campaign_name: string;
    campaign_start_date: string;
    campaign_end_date: string;
    new_site_ui: boolean;
    action?: string;
    object_identifier?: string;
}

enum AllowedButtonTypes {
    save = 'save',
    user_install = 'user_install',
    get_it_now = 'get_it_now',
    watch_demo = 'watch_demo',
    test_drive = 'test_drive',
    learn_more = 'learn_more',
}

export class MarketingAnalyticsEventType {
    // @ts-expect-error * Automated Type Error Fix
    getBody(event): MarketingAnalyticsEvent {
        return this.baseEvent(event);
    }

    // @ts-expect-error * Automated Type Error Fix
    canHandle(event): boolean {
        return event.elementType === 'listingTile' || event.buttonType in AllowedButtonTypes;
    }

    // @ts-expect-error * Automated Type Error Fix
    getAMPCampaignObj(event) {
        return event.sponsoredCampaign || event.listing?.sponsoredCampaign;
    }

    // @ts-expect-error * Automated Type Error Fix
    baseEvent(event): MarketingAnalyticsEvent {
        const { tiles, listing } = event;
        const sponsored = this.isSponsoredSection(tiles?.name);
        const recommended = this.isRecommendedSection(tiles?.name);
        const channel = getChannel(
            event.url,
            event.referrer,
            sponsored,
            recommended,
            !!listing?.sponsoredSearch || !!listing?.sponsored,
        );
        const campObj = sponsored && this.getAMPCampaignObj(event);
        const event_type = this.getEventType(event);

        const eventPayload: MarketingAnalyticsEvent = {
            ...channel, // channel and parent_channel
            oafListingId: getOafIdFromEvent(event),
            event_type: event_type,
            placement_id: campObj?.placement_id,
            campaign_name: campObj?.campaign_name,
            campaign_start_date: campObj?.planned_start_date,
            campaign_end_date: campObj?.planned_end_date,
            topic: this.getTopic(event, event_type),
            authenticated: event.authenticated,
            viewport_height: event.viewport_height,
            viewport_width: event.viewport_width,
            device_height: event.device_height,
            device_width: event.device_width,
            browser: event.browser,
            language: event.language,
            url: event.url,
            referrer: event.referrer,
            primary_key: event.primary_key,
            token: event.token,
            timestamp: event.timestamp,
            date: event.date,
            trace: event.trace,
            new_site_ui: true,
        };

        return eventPayload;
    }

    // @ts-expect-error * Automated Type Error Fix
    getTopic(event, eventType) {
        return eventType === 'Resource View' ? AnalyticsEventTopic.ANALYTICS_USER_ACTIVITY : event.topic;
    }

    // @ts-expect-error * Automated Type Error Fix
    getEventType(event) {
        switch (event.topic) {
            case AnalyticsEventTopic.ANALYTICS_PAGE_LOAD:
                return 'Visit';
            case AnalyticsEventTopic.ANALYTICS_LOAD_IMPRESSION:
                return 'Tile Loaded';
            case AnalyticsEventTopic.ANALYTICS_SHOWN_IMPRESSION:
                return 'Tile View';
            case AnalyticsEventTopic.ANALYTICS_CLICK:
                return this.getClickEventType(event.elementType, event.buttonType, event.context);
            case AnalyticsEventTopic.ANALYTICS_CAROUSEL_SHOWN_IMPRESSION:
                return 'Screenshot View';
            default:
                logger.warn(`Unable to match event type for topic ${event.topic}`);
                return undefined;
        }
    }

    // @ts-expect-error * Automated Type Error Fix
    getClickEventType(elementType, buttonType, context) {
        if (elementType === 'listingTile') {
            return 'Tile Click';
        }
        if (context === MORE_RESOURCES_EVENT_CONTEXT) {
            return 'Resource View';
        }
        return this.mapButtonType(buttonType);
    }

    // @ts-expect-error * Automated Type Error Fix
    mapButtonType(buttonType) {
        switch (buttonType) {
            case AllowedButtonTypes.save:
                return 'Save';
            case AllowedButtonTypes.user_install:
                return 'User Install';
            case AllowedButtonTypes.get_it_now:
                return 'Get It Now';
            case AllowedButtonTypes.watch_demo:
                return 'Watch Demo';
            case AllowedButtonTypes.test_drive:
                return 'Test Drive';
            case AllowedButtonTypes.learn_more:
                return 'Learn More';
            default:
                return undefined;
        }
    }

    isSponsoredSection = (type: string) => {
        return type === SectionTypes.SPONSORED;
    };

    isRecommendedSection = (type: string) => {
        return type === SectionTypes.RECOMMENDATIONS || type === SectionTypes.WHERE_LEFT_OFF;
    };
}
